/** RFC 0345 3a: the account service's enrollment endpoints, for the window
 * and the daemon alike. Credentials ride headers and bodies, never URLs. */
import type { AccountLocation } from './account-location'
import type {
  EnrollmentDecision,
  EnrollmentInbox,
  EnrollmentRequest,
  SignedRecord,
} from './enrollment'

export type EnrollmentStatus =
  | 'started'
  | 'bound'
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'expired'

export class EnrollmentHttpError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message)
  }
}

export function createEnrollmentClient(options: {
  authServer: string
  /** The account session; absent before a desktop handoff is redeemed. */
  token?: () => string | undefined
  send?: typeof fetch
}) {
  const origin = new URL(options.authServer).origin
  const send = options.send ?? fetch
  async function call<T>(
    path: string,
    body?: unknown,
    authenticated = true,
  ): Promise<T> {
    const token = authenticated ? options.token?.() : undefined
    if (authenticated && !token) throw new Error('Sign in with Google first')
    const response = await send(new URL(path, origin), {
      method: body === undefined ? 'GET' : 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: 'omit',
      redirect: 'error',
      cache: 'no-store',
    })
    const parsed = (await response.json().catch(() => null)) as
      | (T & { error?: unknown })
      | null
    if (!response.ok)
      throw new EnrollmentHttpError(
        response.status,
        typeof parsed?.error === 'string'
          ? parsed.error
          : `Enrollment request failed (${response.status})`,
      )
    return parsed as T
  }
  return {
    origin,
    /** The location the first device claimed. A claim, checked by approvers. */
    async location(): Promise<AccountLocation | null> {
      return (
        await call<{ location: AccountLocation | null }>('/keyhive/account')
      ).location
    },
    async claimLocation(location: AccountLocation): Promise<void> {
      await call('/keyhive/account', location)
    },
    async submit(request: SignedRecord<EnrollmentRequest>): Promise<void> {
      await call('/keyhive/enrollment', { request })
    },
    async status(requestId: string): Promise<{
      status: EnrollmentStatus
      decision: EnrollmentDecision | null
    }> {
      return call(
        `/keyhive/enrollment/status?request=${encodeURIComponent(requestId)}`,
      )
    },
    inbox(): EnrollmentInbox {
      return {
        pending: async () =>
          (
            await call<{
              pending: Awaited<ReturnType<EnrollmentInbox['pending']>>
            }>('/keyhive/enrollment/pending')
          ).pending,
        decide: async (requestId, decision) => {
          try {
            await call('/keyhive/enrollment/decision', { requestId, decision })
          } catch (error) {
            // Another device decided first. Its approval stands.
            if (!(error instanceof EnrollmentHttpError && error.status === 409))
              throw error
          }
        },
      }
    },
    /** Desktop handoff, before any account session exists. */
    handoff: {
      /** Signed by the ephemeral key over startMessage. */
      start: (body: {
        requestId: string
        ephemeralKey: string
        cardHash: string
        issuedAt: number
        signature: string
      }) => call<{ signIn: string }>('/keyhive/enrollment/start', body, false),
      /** Signed by the ephemeral key over challengeMessage. */
      challenge: (body: {
        requestId: string
        issuedAt: number
        signature: string
      }) =>
        call<{ challenge: string }>(
          '/keyhive/enrollment/challenge',
          body,
          false,
        ),
      redeem: (body: {
        requestId: string
        challenge: string
        signature: string
      }) =>
        call<
          | { status: 'waiting' }
          | {
              status?: undefined
              accountId: string
              email: string
              token: string
              cardHash: string
              location: AccountLocation | null
            }
        >('/keyhive/enrollment/redeem', body, false),
    },
  }
}
export type EnrollmentClient = ReturnType<typeof createEnrollmentClient>

/** The bytes the desktop's ephemeral key signs. Each matches the auth-worker. */
export const startMessage = (
  requestId: string,
  ephemeralKey: string,
  cardHash: string,
  issuedAt: number,
) =>
  new TextEncoder().encode(
    `mod:enroll-start:v1\n${requestId}\n${ephemeralKey}\n${cardHash}\n${issuedAt}`,
  )
export const challengeMessage = (requestId: string, issuedAt: number) =>
  new TextEncoder().encode(`mod:enroll-challenge:v1\n${requestId}\n${issuedAt}`)
export const redeemMessage = (
  requestId: string,
  challenge: string,
  cardHash: string,
) =>
  new TextEncoder().encode(
    `mod:enroll-redeem:v1\n${requestId}\n${challenge}\n${cardHash}`,
  )
